import {
  StyledClose,
  StyledFilterRow,
} from 'modules/messagingInsights/components/FilterList/Styled';

import { WORKFLOW_TRIGGERS } from 'modules/workflows/constants/base';
import { ComponentType, useMemo } from 'react';
import { BUTTON_VARIANTS } from 'ui/button';
import { Close16 } from 'ui/icons/action/Close16';
import { EQUALS, IS_NOT_SET, IS_SET } from 'utils/comparatorTypes';
import { hasValidationError } from '../../RuleBuilder/hooks/useRuleBuilder.utils';
import { ContainsCloningError, SearchQueryValidationErrors } from '../../RuleBuilder/types';
import { ComparatorArray, comparatorIcons } from '../FilterInputsList.constants';
import {
  FilterSegmentSelectInputOption,
  NestComponentProps,
  Query,
} from '../FilterInputsList.types';
import { createFilterSelectOption } from '../utils';
import { FilterSegmentSelectInput } from './FilterSegmentSelectInput';
import { FilterSegmentTypeaheadSelect } from './FilterSegmentTypeaheadSelect';
import { FilterValueInput } from './FilterValueInput';

const content = {
  propertySelectPrompt: 'Select a property',
  comparatorSelectPrompt: 'Select a comparator',
  valueSelectPrompt: 'Select...',
};

const parseSelectedProperty = (
  property: FilterSegmentSelectInputOption,
  query: Partial<Query> & ContainsCloningError,
) => ({
  ...property,
  field: property?.id,
  fieldType: property?.fieldType,
  comparatorType: EQUALS,
  value: null,
  valueDisplay: query?.containsCloningError ? query?.value : undefined,
});

type FilterInputProps = {
  comparators: ComparatorArray;
  properties: FilterSegmentSelectInputOption[];
  onChange: (query: Partial<Query>, idx: number) => void;
  id: string;
  idx: number;
  onRemove: () => void;
  query: Partial<Query> & ContainsCloningError;
  errors?: SearchQueryValidationErrors;
  trigger: string;
  canDelete?: boolean;
  NestComponent?: ComponentType<React.PropsWithChildren<NestComponentProps>> | null;
  DeleteFilterComponent?: ComponentType<React.PropsWithChildren<unknown>>;
  hasError: boolean; // old error referring to value error
  isDisabled?: boolean;
  isDarkTheme?: boolean;
  isPropertyChanges?: boolean;
};

export const FilterInput = ({
  comparators,
  errors = {},
  hasError, // deprecated, kept in place to not break anything
  id,
  idx,
  onChange,
  onRemove,
  properties,
  query,
  trigger,
  canDelete = true,
  NestComponent,
  DeleteFilterComponent = Close16,
  isDisabled = false,
  isDarkTheme = false,
  isPropertyChanges = false,
}: FilterInputProps) => {
  const { field, value, comparatorType } = query;
  const { field: fieldError, value: valueError, comparatorType: comparatorError } = errors;

  const hasFieldDisplay = field || (field === null && fieldError?.displayValue);
  const hasPropertyError = hasValidationError(fieldError);
  const hasValueError = hasValidationError(valueError);
  const hasComparatorError = hasValidationError(comparatorError);
  const comparatorOptions = useMemo(
    () =>
      comparators.map((comparator) => {
        const { type, displayName } = comparator;
        const icon = comparatorIcons[type];

        return createFilterSelectOption(type, displayName, {
          Suffix: icon || '',
          labelInTriggerButton: icon || displayName?.toLowerCase(),
        });
      }),
    [comparators],
  );
  const propertyProps = useMemo(() => {
    return {
      id: `${id}-property-select-input`,
      isFilterable: true,
      isDisabled,
      options: properties,
      placeholder: fieldError?.displayValue || content.propertySelectPrompt,
      selectedOptionIds: [field as string],
      onSelect: ([option]: FilterSegmentSelectInputOption[]) => {
        onChange({ ...query, ...parseSelectedProperty(option, query) }, idx);
      },
    };
  }, [field, properties, onChange, id, query, idx]);
  return (
    <StyledFilterRow isDarkTheme={isDarkTheme} isPropertyChanges={isPropertyChanges}>
      {NestComponent && <NestComponent idx={idx} />}

      {/** Filter Property Input */}
      {trigger === WORKFLOW_TRIGGERS.RECEIVED_API ? (
        <FilterSegmentTypeaheadSelect {...propertyProps} hasError={hasPropertyError} />
      ) : (
        <FilterSegmentSelectInput {...propertyProps} hasError={hasPropertyError} />
      )}

      {/** Filter Comparator Input */}
      {hasFieldDisplay && (
        <FilterSegmentSelectInput
          options={comparatorOptions}
          selectedOptionIds={comparatorType ? [comparatorType] : []}
          onSelect={([selectedOption]) =>
            onChange({ ...query, comparatorType: selectedOption?.id }, idx)
          }
          id={`${id}-comparator-select-input`}
          hasError={hasComparatorError}
          placeholder={comparatorError?.displayValue || content.comparatorSelectPrompt}
          isDisabled={isDisabled}
        />
      )}

      {/** Filter Value Input */}
      {hasFieldDisplay && ![IS_SET, IS_NOT_SET].includes(comparatorType as string) && (
        <FilterValueInput
          data-test={`filter-${idx}-value`}
          data-value={value}
          id={'filter-value-string-input'}
          onChange={(val) => onChange({ ...query, value: val }, idx)}
          hasError={hasValueError || hasError}
          value={value?.toString() || ''}
          query={query}
          isDisabled={isDisabled}
          placeholder={valueError?.displayValue || ''}
        />
      )}

      {/** Filter Delete Button */}
      {canDelete && (
        <StyledClose variant={BUTTON_VARIANTS.GHOST} onClick={onRemove} isDarkTheme={isDarkTheme}>
          <DeleteFilterComponent />
        </StyledClose>
      )}
    </StyledFilterRow>
  );
};
